import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart3, TrendingUp, TrendingDown, MapPin, Shield, AlertTriangle,
  Users, Phone, Eye, HelpCircle, Navigation,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Plain-English hint (Amigos brass palette) */
const Hint = ({ text }) => (
  <div className="amg-hint">
    <HelpCircle size={15} />
    <span>{text}</span>
  </div>
);

/* Horizontal bar with animated fill (Lone Star's SimpleBar port) */
const SimpleBar = ({ label, value, max, color = 'var(--gold)' }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: '0.7rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontFamily: 'var(--font-alt)', letterSpacing: '0.04em' }}>{value}</span>
      </div>
      <div style={{ height: 10, background: 'rgba(255,255,255,0.05)', borderRadius: 5, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 5, transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1)' }} />
      </div>
    </div>
  );
};

const Reports = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('amg_token');
    const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    axios.get(`${API}/analytics/overview`, cfg)
      .then((res) => setData(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)', fontSize: '1.3rem' }}>Loading your analytics report\u2026</div>;
  }
  if (!data) {
    return <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>Failed to load analytics.</div>;
  }

  const chargeMax = Math.max(...Object.values(data.charge_breakdown || {}), 1);
  const areaMax = Math.max(...Object.values(data.area_breakdown || {}), 1);
  const bondMax = Math.max(...Object.values(data.bond_ranges || {}), 1);
  const relMax = Math.max(...Object.values(data.relation_breakdown || {}), 1);
  const ageMax = Math.max(...Object.values(data.age_groups || {}), 1);

  const weekTrend = (data.this_week || 0) - (data.last_week || 0);
  const dailyMax = Math.max(...(data.daily_stats || []).map((d) => d.submissions), 1);

  return (
    <div data-testid="analytics-view">
      <div style={{ marginBottom: '2rem' }}>
        <h1 className="dash-content__title" data-testid="analytics-title">
          <BarChart3 size={28} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 10, color: 'var(--gold)' }} />
          YOUR BUSINESS REPORT
        </h1>
        <p className="dash-content__subtitle">
          Everything you need to know about how your business is doing, all in one place.
        </p>
      </div>

      <Hint text="This report shows you how many people are reaching out, what they're getting arrested for, where they're coming from, and how your website is performing. Use it to understand your business at a glance." />

      {/* ── Top-Level Numbers ─────────────────────────────── */}
      <div className="dash-stats" data-testid="analytics-top-stats" style={{ marginBottom: '2rem' }}>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><Shield size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> TOTAL CLIENTS</div>
          <div className="dash-stat-card__value">{data.total_submissions || 0}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>People who filled out forms</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><Eye size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> WEBSITE VISITORS</div>
          <div className="dash-stat-card__value">{data.page_views || 0}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>How many people visited your site</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><Phone size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> PHONE CALLS</div>
          <div className="dash-stat-card__value">{data.call_clicks || 0}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>People who tapped &quot;Call Now&quot;</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><Navigation size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> DIRECTIONS</div>
          <div className="dash-stat-card__value">{data.direction_clicks || 0}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>People who looked up your address</div>
        </div>
      </div>

      {/* ── Week Comparison ──────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }} data-testid="week-comparison">
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--text-primary)', letterSpacing: '0.06em', textTransform: 'uppercase', margin: 0 }}>THIS WEEK</h3>
            <span style={{
              fontSize: '0.72rem',
              padding: '0.15rem 0.55rem',
              background: weekTrend >= 0 ? 'rgba(123, 160, 94, 0.1)' : 'rgba(122, 30, 40, 0.1)',
              border: `1px solid ${weekTrend >= 0 ? 'rgba(123, 160, 94, 0.3)' : 'rgba(122, 30, 40, 0.3)'}`,
              color: weekTrend >= 0 ? 'var(--success)' : 'var(--red-bright)',
              fontWeight: 700,
              fontFamily: 'var(--font-alt)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}>
              {weekTrend >= 0
                ? <TrendingUp size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />
                : <TrendingDown size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />}
              {weekTrend >= 0 ? '+' : ''}{weekTrend} vs last week
            </span>
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: 'var(--gold)', lineHeight: 1, textShadow: '0 0 24px rgba(177, 133, 74, 0.3)' }}>{data.this_week || 0}</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>new submissions this week</div>
        </div>
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--text-primary)', letterSpacing: '0.06em', marginBottom: '0.75rem', textTransform: 'uppercase' }}>LAST WEEK</h3>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: 'var(--text-secondary)', lineHeight: 1 }}>{data.last_week || 0}</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>submissions the week before</div>
        </div>
      </div>

      {/* ── Daily 30-Day Chart ───────────────────────────── */}
      <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)', marginBottom: '2rem' }} data-testid="daily-30-chart">
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'var(--text-primary)', letterSpacing: '0.06em', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Daily Activity — Last 30 Days</h3>
        <Hint text="Each bar below represents one day. Taller bars mean more people submitted forms that day. This helps you see which days of the week are busiest." />
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 100, paddingTop: '0.5rem' }}>
          {(data.daily_stats || []).map((d, i) => (
            <div
              key={i}
              title={`${d.label}: ${d.submissions}`}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
            >
              <div style={{
                width: '100%',
                minHeight: 3,
                height: `${Math.max(3, (d.submissions / dailyMax) * 80)}px`,
                background: d.submissions > 0
                  ? 'linear-gradient(180deg, var(--gold), #6c4f1c)'
                  : 'rgba(255,255,255,0.05)',
                transition: 'height 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                borderRadius: '2px 2px 0 0',
                boxShadow: d.submissions > 0 ? '0 0 8px rgba(177, 133, 74, 0.28)' : 'none',
              }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.35rem' }}>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontFamily: 'var(--font-alt)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>30 days ago</span>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontFamily: 'var(--font-alt)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Today</span>
        </div>
      </div>

      {/* ── Four Breakdown Panels ─────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }} data-testid="breakdowns">

        {/* Charges */}
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }} data-testid="charge-breakdown">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'var(--text-primary)', letterSpacing: '0.05em', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
            <AlertTriangle size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8, color: 'var(--red-bright)' }} />
            Types of Charges
          </h3>
          <Hint text="This shows what people are most commonly getting arrested for. DWI, assault, and drug offenses are typical for Montgomery County." />
          {Object.keys(data.charge_breakdown || {}).length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No charge data yet. This will populate as forms come in.</p>
          ) : (
            Object.entries(data.charge_breakdown).map(([k, v]) => (
              <SimpleBar key={k} label={k} value={v} max={chargeMax} color="var(--red-bright)" />
            ))
          )}
        </div>

        {/* Bond Ranges */}
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }} data-testid="bond-ranges">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'var(--text-primary)', letterSpacing: '0.05em', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
            Bond Amount Ranges
          </h3>
          <Hint text="This shows you the dollar ranges of the bonds people are applying for. Higher bonds mean bigger cases and bigger premiums for you." />
          {Object.entries(data.bond_ranges || {}).filter(([, v]) => v > 0).length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No bond data yet.</p>
          ) : (
            Object.entries(data.bond_ranges || {}).filter(([, v]) => v > 0).map(([k, v]) => (
              <SimpleBar key={k} label={k} value={v} max={bondMax} color="var(--gold)" />
            ))
          )}
        </div>

        {/* Service Area */}
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }} data-testid="area-breakdown">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'var(--text-primary)', letterSpacing: '0.05em', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
            <MapPin size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8, color: 'var(--gold)' }} />
            Where Clients Come From
          </h3>
          <Hint text="These are the cities and neighborhoods your clients live in. This tells you which areas need you the most." />
          {Object.keys(data.area_breakdown || {}).length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No location data yet. This populates from form addresses.</p>
          ) : (
            Object.entries(data.area_breakdown).map(([k, v]) => (
              <SimpleBar key={k} label={k} value={v} max={areaMax} color="var(--gold)" />
            ))
          )}
        </div>

        {/* Relationship */}
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }} data-testid="relation-breakdown">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'var(--text-primary)', letterSpacing: '0.05em', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
            <Users size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8, color: 'var(--gold)' }} />
            Who Is Calling For Help
          </h3>
          <Hint text="This shows you the relationship between the person filling out the form and the person in jail. Most commonly it's a spouse, parent, or friend." />
          {Object.keys(data.relation_breakdown || {}).length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No relationship data yet.</p>
          ) : (
            Object.entries(data.relation_breakdown).map(([k, v]) => (
              <SimpleBar key={k} label={k} value={v} max={relMax} color="#7db6f0" />
            ))
          )}
        </div>
      </div>

      {/* ── Demographics ─────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }} data-testid="demographics">
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }} data-testid="age-groups">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'var(--text-primary)', letterSpacing: '0.05em', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Age Groups</h3>
          <Hint text="Age range of the defendants (the people in jail) based on their date of birth on the form." />
          {Object.entries(data.age_groups || {}).filter(([, v]) => v > 0).length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>No age data yet.</p>
          ) : (
            Object.entries(data.age_groups || {}).filter(([, v]) => v > 0).map(([k, v]) => (
              <SimpleBar key={k} label={k} value={v} max={ageMax} color="#7db6f0" />
            ))
          )}
        </div>
        <div style={{ padding: '1.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }} data-testid="gender-breakdown">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'var(--text-primary)', letterSpacing: '0.05em', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Gender</h3>
          <Hint text="Gender of the people filling out the bond application (from the form data)." />
          {Object.keys(data.sex_breakdown || {}).length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Gender is not currently collected on the intake form. Add a &ldquo;Sex&rdquo; field to the intake to populate this section.</p>
          ) : (
            Object.entries(data.sex_breakdown || {}).map(([k, v]) => (
              <SimpleBar
                key={k}
                label={k === 'M' ? 'Male' : k === 'F' ? 'Female' : k}
                value={v}
                max={Math.max(...Object.values(data.sex_breakdown || {}), 1)}
                color="var(--success)"
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
